/**
 * Sanitizer for a metadata trait/value fragment, split out of `params.ts` (a
 * `"use client"` module importing `next/navigation`) so it is a plain
 * function with its own test.
 *
 * Control characters are stripped (same rule as `params.ts`'s own
 * `readQuery`), the result is length-capped, and everything else is left
 * untouched: a trait value is free text ("Ocean Blue", "آبی اقیانوسی"), not an
 * identifier, so spaces and any script pass through unchanged. Membership
 * against the real option list (derived from actual API data) is the
 * caller's job — same two-stage split `network_id`/`category` already use.
 */
export function readFacetText(raw: string | null): string | null {
  if (!raw) return null;
  const cleaned = Array.from(raw)
    .filter((ch) => {
      const code = ch.codePointAt(0) ?? 0;
      return code > 31 && code !== 127;
    })
    .join("")
    .trim();
  return cleaned ? cleaned.slice(0, 80) : null;
}

/**
 * The trait→value invariant, enforced at the schema layer rather than left to
 * each call site to remember. A `value` chosen under one `trait_type` has no
 * guaranteed meaning under another, so a patch that changes `trait` WITHOUT
 * also specifying `value` must clear the previous value rather than let it
 * survive alongside the new trait — e.g. a stale `trait=Material&value=Black`
 * pair from selecting "Material" after "Color: Black" was already applied.
 * A patch that explicitly sets both (or sets `value` alone, independent of
 * `trait`) is left untouched.
 */
export function resolveTraitValuePatch<T extends { trait?: string | null; value?: string | null }>(
  patch: T
): T {
  if (patch.trait === undefined || patch.value !== undefined) return patch;
  return { ...patch, value: null };
}
